import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { getServices } from '../services/api';
import LazyImage from '../components/LazyImage';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const categories = [
    { id: 'all', name: 'All' },
    { id: 'manicure', name: 'Manicures' },
    { id: 'extensions', name: 'Extensions' },
    { id: 'art', name: 'Nail Art' },
    { id: 'other', name: 'Other' }
  ];

  const filteredServices = activeCategory === 'all' 
    ? services 
    : services.filter(s => s.category === activeCategory);

  return (
    <main>
      {/* Hero */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="text-center max-w-xl mx-auto">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Our Services</p>
            <h1 className="hero-medium mb-4">Services & Pricing</h1>
            <p className="body-large text-[var(--text-secondary)]">
              From classic manicures to custom nail art, discover our full range of services.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container">
          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10 sm:mb-12">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2.5 text-sm rounded-full transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[var(--text-primary)] text-[var(--bg-primary)]'
                    : 'bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex gap-4 p-5 bg-[var(--bg-card)] rounded-xl">
                  <div className="w-28 h-28 skeleton rounded-lg flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-5 skeleton w-2/3" />
                    <div className="h-4 skeleton w-full" />
                    <div className="h-4 skeleton w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
              {filteredServices.map((service) => (
                <div 
                  key={service.id} 
                  className="flex flex-col sm:flex-row gap-4 sm:gap-5 p-5 bg-[var(--bg-card)] rounded-xl transition-all hover:shadow-sm group"
                >
                  <div className="w-full sm:w-32 h-32 flex-shrink-0 overflow-hidden rounded-lg">
                    <LazyImage
                      src={service.image_url}
                      alt={service.name}
                      className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                      aspectRatio="1/1"
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <div>
                        <h3 className="heading-3">{service.name}</h3>
                        {service.is_popular && (
                          <span className="inline-block mt-1 px-2.5 py-0.5 text-xs bg-[var(--accent-blush)] text-[var(--text-heading)] rounded-full">
                            Popular
                          </span>
                        )}
                      </div>
                      <p className="text-xl font-medium text-[var(--text-heading)]" style={{fontFamily: "'Cormorant Garamond', serif"}}>
                        ${service.price}
                        {service.price_note && (
                          <span className="text-sm font-normal text-[var(--text-secondary)]"> {service.price_note}</span>
                        )}
                      </p>
                    </div>
                    <p className="body-small text-[var(--text-secondary)] mb-3 flex-1 line-clamp-2">
                      {service.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[var(--text-light)]">
                        <Clock size={14} />
                        <span className="body-small">{service.duration}</span>
                      </div>
                      <Link 
                        to={`/book?service=${service.id}`}
                        className="btn-text text-sm"
                      >
                        Book Now
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredServices.length === 0 && (
            <div className="text-center py-16">
              <p className="body-regular text-[var(--text-secondary)]">
                No services found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Policies */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Booking Policies</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { title: 'Cancellation', desc: 'Please provide at least 24 hours notice for cancellations or rescheduling.' },
                { title: 'Deposits', desc: 'A deposit may be required for certain services or new clients.' },
                { title: 'Arrival', desc: 'Please arrive on time. Late arrivals may result in shortened service time.' },
                { title: 'Payment', desc: 'We accept cash, e-transfer, and major credit cards.' }
              ].map((item, i) => (
                <div key={i} className="p-5 bg-[var(--bg-card)] rounded-xl">
                  <h3 className="heading-3 mb-2">{item.title}</h3>
                  <p className="body-small text-[var(--text-secondary)]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center max-w-lg mx-auto">
            <h2 className="heading-1 mb-4">Ready to Book?</h2>
            <p className="body-regular text-[var(--text-secondary)] mb-8">
              Choose your preferred service and book your appointment online.
            </p>
            <Link to="/book" className="btn-primary">
              Book Appointment
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Services;
